import { useState } from 'react';
import type { ExportFormat, ExportSettings } from '../types';

/** Whether the browser supports writing image blobs to the OS
 *  clipboard. PNG is the only format with broad support; the dialog
 *  surfaces the option only when this is true. */
const SUPPORTS_CLIPBOARD_WRITE =
  typeof navigator !== 'undefined' &&
  typeof navigator.clipboard?.write === 'function' &&
  typeof ClipboardItem !== 'undefined';

/** Whether the browser supports the Web Share API with files. Used on
 *  mobile (coarse-pointer) to surface "Share" instead of "Clipboard"
 *  — same destination button, more useful affordance on touch devices. */
const SUPPORTS_FILE_SHARE =
  typeof navigator !== 'undefined' &&
  typeof navigator.share === 'function' &&
  typeof navigator.canShare === 'function';

const IS_COARSE_POINTER =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

/** True when the third destination button should be shown at all. */
export const SUPPORTS_CLIPBOARD_OR_SHARE = SUPPORTS_CLIPBOARD_WRITE || SUPPORTS_FILE_SHARE;

export type ExportDestination = 'local' | 'fias' | 'clipboard';

interface ExportDialogProps {
  onClose: () => void;
  onExportLocal: (
    fileName: string,
    format: ExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => void | Promise<void>;
  onExportFias: (settings: ExportSettings, fileName: string) => void | Promise<void>;
  /** Copy the export to the OS clipboard (desktop) or trigger the
   *  native share sheet (mobile). Optional — dialog hides the
   *  destination option entirely when not provided. */
  onExportClipboard?: (
    fileName: string,
    format: ExportFormat,
    quality: number,
    target?: { width?: number; height?: number },
  ) => void | Promise<void>;
  defaultFileName: string;
  canvasWidth: number;
  canvasHeight: number;
  /**
   * Pre-select the destination toggle. Used when the dialog is opened from
   * the close-confirmation flow with a chosen destination.
   */
  defaultDestination?: ExportDestination;
}

export function ExportDialog({
  onClose,
  onExportLocal,
  onExportFias,
  onExportClipboard,
  defaultFileName,
  canvasWidth,
  canvasHeight,
  defaultDestination = 'local',
}: ExportDialogProps) {
  const [fileName, setFileName] = useState(defaultFileName);
  const [format, setFormat] = useState<ExportFormat>('png');
  const [quality, setQuality] = useState(92);
  const [width, setWidth] = useState(canvasWidth);
  const [height, setHeight] = useState(canvasHeight);
  const [maintainAspectRatio, setMaintainAspectRatio] = useState(true);
  const [destination, setDestination] = useState<ExportDestination>(defaultDestination);
  const [isExporting, setIsExporting] = useState(false);

  // The third destination is "Share" on mobile (where the native
  // share sheet is the right affordance for "send this to another
  // app") and "Copy to clipboard" on desktop. Same outcome from the
  // dialog's perspective — the parent picks the right path.
  const clipboardDestinationLabel =
    IS_COARSE_POINTER && SUPPORTS_FILE_SHARE ? 'Share' : 'Copy to clipboard';
  const showClipboardDestination =
    typeof onExportClipboard === 'function' && SUPPORTS_CLIPBOARD_OR_SHARE;

  const aspectRatio = canvasWidth / canvasHeight;

  const handleWidthChange = (w: number) => {
    setWidth(w);
    if (maintainAspectRatio) {
      setHeight(Math.round(w / aspectRatio));
    }
  };

  const handleHeightChange = (h: number) => {
    setHeight(h);
    if (maintainAspectRatio) {
      setWidth(Math.round(h * aspectRatio));
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // Only forward target dimensions when they actually differ from
      // the source canvas — otherwise the helper passes the native
      // image resolution path through unchanged.
      const target =
        width === canvasWidth && height === canvasHeight ? undefined : { width, height };
      if (destination === 'local') {
        await onExportLocal(fileName, format, quality / 100, target);
      } else if (destination === 'fias') {
        await onExportFias({ format, quality, width, height, maintainAspectRatio }, fileName);
      } else if (destination === 'clipboard' && onExportClipboard) {
        await onExportClipboard(fileName, format, quality / 100, target);
      }
    } finally {
      setIsExporting(false);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md p-6 space-y-4">
        <h2 className="text-lg font-semibold">Save Image As&hellip;</h2>

        {/* File name */}
        <label className="block space-y-1">
          <span className="text-sm font-medium">File name</span>
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
          />
        </label>

        {/* Format */}
        <label className="block space-y-1">
          <span className="text-sm font-medium">Format</span>
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value as ExportFormat)}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded-md"
          >
            <option value="png">PNG (lossless)</option>
            <option value="jpeg">JPEG</option>
            <option value="webp">WebP</option>
          </select>
        </label>

        {/* Quality (for lossy formats) */}
        {format !== 'png' && (
          <label className="block space-y-1">
            <span className="text-sm font-medium">Quality: {quality}%</span>
            <input
              type="range"
              min={10}
              max={100}
              value={quality}
              onChange={(e) => setQuality(Number(e.target.value))}
              className="w-full"
            />
          </label>
        )}

        {/* Dimensions */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Dimensions</span>
          <div className="flex gap-2 items-center">
            <input
              type="number"
              value={width}
              onChange={(e) => handleWidthChange(Number(e.target.value))}
              className="w-24 px-2 py-1 text-sm bg-background border border-border rounded-md"
              min={1}
            />
            <span className="text-muted-foreground">x</span>
            <input
              type="number"
              value={height}
              onChange={(e) => handleHeightChange(Number(e.target.value))}
              className="w-24 px-2 py-1 text-sm bg-background border border-border rounded-md"
              min={1}
            />
            <span className="text-xs text-muted-foreground">px</span>
          </div>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={maintainAspectRatio}
              onChange={(e) => setMaintainAspectRatio(e.target.checked)}
              className="rounded"
            />
            <span className="text-xs text-muted-foreground">Maintain aspect ratio</span>
          </label>
        </div>

        {/* Destination */}
        <div className="space-y-2">
          <span className="text-sm font-medium">Save to</span>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              className={`flex-1 min-w-[120px] py-2 text-sm rounded-md border ${
                destination === 'local'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              onClick={() => setDestination('local')}
            >
              Local download
            </button>
            <button
              type="button"
              className={`flex-1 min-w-[120px] py-2 text-sm rounded-md border ${
                destination === 'fias'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:bg-accent'
              }`}
              onClick={() => setDestination('fias')}
            >
              Fias files
            </button>
            {showClipboardDestination && (
              <button
                type="button"
                className={`flex-1 min-w-[120px] py-2 text-sm rounded-md border ${
                  destination === 'clipboard'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-accent'
                }`}
                onClick={() => setDestination('clipboard')}
              >
                {clipboardDestinationLabel}
              </button>
            )}
          </div>
          {destination === 'clipboard' &&
            format !== 'png' &&
            SUPPORTS_CLIPBOARD_WRITE &&
            !IS_COARSE_POINTER && (
              <p className="text-xs text-muted-foreground">
                Most browsers only support PNG on the clipboard. Switch format if the copy fails.
              </p>
            )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md border border-border text-muted-foreground hover:bg-accent"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
